import React from 'react';
import { motion } from 'framer-motion';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock, faTrophy, faUsers } from '@fortawesome/free-solid-svg-icons';

const TestReviewScore = ({ 
  score, 
  avgTime, 
  theme, 
  onCommunityClick, 
  showCommunityIcon = true,
  showProgressIndicator = false,
  progressData = null,
  achievementBadge = null
}) => {
  const styles = {
    container: {
      background: theme?.background?.secondary,
      borderRadius: '1.5rem',
      padding: '2.5rem',
      textAlign: 'center',
      position: 'relative',
      overflow: 'hidden',
      maxWidth: '800px',
      margin: '0 auto',
    },
    
    content: {
      position: 'relative',
      zIndex: 1,
    },
    
    communityIcon: {
      position: 'absolute',
      top: '1rem',
      right: '1rem',
      width: '3.25rem',
      height: '3.25rem',
      borderRadius: '0.5rem',
      background: `${theme?.primary?.blue?.[500]}15`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      zIndex: 2,
    },
    
    communityIconHover: {
      background: `${theme?.primary?.blue?.[500]}50`,
      transform: 'scale(1.05)',
    },
    
    communityIconSvg: {
      fontSize: '1.25rem',
      color: theme?.primary?.blue?.[500],
    },
    
    scoreMain: {
      fontSize: '6.5rem',
      fontWeight: '800',
      lineHeight: '1',
      marginBottom: '0.5rem',
      color: getScoreColor(),
    },
    
    scoreLabel: {
      fontSize: '1.125rem',
      fontWeight: '600',
      color: theme?.text?.primary,
      marginBottom: '1rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
    },
    
    scoreIcon: {
      color: theme?.primary?.blue?.[500],
    },
    
    avgTime: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
      fontSize: '0.875rem',
      color: theme?.text?.secondary,
      marginTop: '1rem',
    },
    
    timeIcon: {
      fontSize: '1rem',
      color: theme?.text?.tertiary,
    },

    // Progress indicator styles
    progressIndicator: {
      background: 'rgba(255, 255, 255, 0.1)',
      borderRadius: '0.75rem',
      padding: '1rem',
      marginBottom: '1.5rem',
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
    },

    progressIcon: {
      width: '2.5rem',
      height: '2.5rem',
      background: theme?.dashboard?.status?.correct || '#10b981',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      fontWeight: 'bold',
    },

    progressText: {
      color: 'white',
      flex: 1,
    },

    progressTitle: {
      fontSize: '1rem',
      marginBottom: '0.25rem',
      fontWeight: '600',
    },

    progressSubtitle: {
      fontSize: '0.875rem',
      opacity: 0.9,
    },

    achievementBadge: {
      background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
      padding: '0.5rem 1rem',
      borderRadius: '1.25rem',
      fontSize: '0.75rem',
      fontWeight: '600',
      marginLeft: 'auto',
    },
  };

  function getScoreColor() {
    if (score >= 80) return theme?.primary?.green?.[500];
    if (score >= 50) return theme?.primary?.yellow?.[500];
    return theme?.primary?.red?.[500];
  }

  const [isHovered, setIsHovered] = React.useState(false);

  return (
    <div>
      {/* Progress Indicator */}
      {showProgressIndicator && progressData && (
        <motion.div
          style={styles.progressIndicator}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div style={styles.progressIcon}>
            ↗
          </div>
          <div style={styles.progressText}>
            <div style={styles.progressTitle}>{progressData.title}</div>
            <div style={styles.progressSubtitle}>{progressData.subtitle}</div>
          </div>
          {achievementBadge && (
            <div style={styles.achievementBadge}>
              {achievementBadge}
            </div>
          )}
        </motion.div>
      )}

      <motion.div
        style={styles.container}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
      >
        {/* Community Icon */}
        {showCommunityIcon && (
          <motion.div
            style={{
              ...styles.communityIcon,
              ...(isHovered ? styles.communityIconHover : {})
            }}
            onClick={onCommunityClick}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <FontAwesomeIcon icon={faUsers} style={styles.communityIconSvg} />
          </motion.div>
        )}
        
        <div style={styles.content}>
          <motion.div
            style={styles.scoreMain}
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ 
              duration: 0.8, 
              ease: "easeOut",
              delay: 0.2 
            }}
          >
            {score}%
          </motion.div>
          
          <div style={styles.scoreLabel}>
            <FontAwesomeIcon icon={faTrophy} style={styles.scoreIcon} />
            Test Score
          </div>
          
          <div style={styles.avgTime}>
            <FontAwesomeIcon icon={faClock} style={styles.timeIcon} />
            Average time per question: {avgTime}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default TestReviewScore;